import Flickity from "flickity-imagesloaded";
import React from "react";
import { addProductToBasket } from "tsi-common-react/src/apps/configurator/actions";
import {
    IConcreteBundle,
    IProduct,
} from "tsi-common-react/src/models/catalogue";
import { ProductUpsellModalBlockSlide } from "tsi-common-react/src/models/streamfield-blocks";
import { trackAddToBasketEvent } from "tsi-common-react/src/utils/analytics";

import UpsellModalSlide from "./UpsellModalSlide";

const isAvailable = (bundle: IConcreteBundle): boolean => {
    const product = bundle.suggested_products[0];
    return product.availability.is_available_to_buy;
};

const initFlickity = (
    slidesElem: HTMLDivElement | null,
    flickityRef: React.MutableRefObject<Flickity | null>,
    slides: ReadonlyArray<ProductUpsellModalBlockSlide>,
): void => {
    if (!slidesElem) {
        return;
    }
    if (flickityRef.current) {
        flickityRef.current.destroy();
    }
    const initialSlide = slides.findIndex((s) => {
        return s.initial_mobile_slide;
    });
    if (slidesElem.childNodes.length > 1) {
        flickityRef.current = new Flickity(slidesElem, {
            cellAlign: "center",
            wrapAround: false,
            imagesLoaded: true,
            prevNextButtons: false,
            initialIndex: initialSlide,
            watchCSS: true,
        });
    }
};

/**
 * A collection of UpsellModalSlide components
 */
const UpsellModalSlideCollection = (props: {
    slides: ReadonlyArray<ProductUpsellModalBlockSlide>;
    bundles: readonly IConcreteBundle[];
    onProceed: () => void;
}): React.ReactNode => {
    const [buyIsDisabled, setBuyIsDisabled] = React.useState(false);
    const flickityRef = React.useRef<Flickity | null>(null);
    const slidesElemRef = React.useRef<HTMLDivElement | null>(null);

    const handleAddToBasket = async (product: IProduct) => {
        setBuyIsDisabled(true);
        try {
            await addProductToBasket(product.url);
        } catch (e) {
            setBuyIsDisabled(false);
            return;
        }
        trackAddToBasketEvent(product);
        props.onProceed();
    };

    React.useEffect(() => {
        initFlickity(slidesElemRef.current, flickityRef, props.slides);

        return () => {
            if (flickityRef.current) {
                flickityRef.current.destroy();
            }
        };
    });

    if (!props.slides) {
        return null;
    }

    const slides = props.slides.map((slide, i) => {
        const isBundle = slide.slide_bundle?.id !== 0;
        let bundle: IConcreteBundle | null = null;
        if (isBundle) {
            bundle = props.bundles.filter((b) => {
                return b.bundle_group.id === slide.slide_bundle?.id;
            })[0];
        }
        if (!bundle) {
            return null;
        }
        // Skip rendering if product is not available
        if (!isAvailable(bundle)) {
            return null;
        }
        return (
            <UpsellModalSlide
                key={i}
                isBundle={isBundle}
                bundle={bundle}
                imagePath={slide.image.url}
                imageAlt={slide.image.title}
                ctaPath={slide.slide_link}
                ctaCopy={slide.slide_cta}
                buyIsDisabled={buyIsDisabled}
                onAddToBasket={handleAddToBasket}
            />
        );
    });

    return (
        <div
            className="upsell-modal__slides"
            ref={(ref) => {
                slidesElemRef.current = ref;
            }}
        >
            {slides}
        </div>
    );
};

export default UpsellModalSlideCollection;
